/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import type { BibleBookId, BibleDeeplinks } from "@packages/sdk";
import { useLocalStorageState } from "@packages/sdk";
import {
  constantSemanticColors,
  RecommendationDeeplink,
  spacing,
  useTheme,
} from "@packages/ui/emotion";
import type { MouseEventHandler } from "react";
import { Suspense, useContext, useEffect, useRef } from "react";

import { useBibleHTML } from "../../../lib";
import { BibleEntry } from "../BibleEntryTracker";
import { BibleLanguageContext } from "../BibleLanguageProvider";

const bodyStyle: Interpolation = {
  margin: "auto",
  maxWidth: spacing.maxReaderWidth,
  padding: "40px 15px 150px",
  "& *": {
    transitionProperty: "background, color",
    transitionDuration: "500ms",
    transitionTimingFunction: "ease-out",
  },
};
const bodyTopStyle: Interpolation = {
  padding: "40px 15px 0",
};
const bodyMiddleStyle: Interpolation = {
  padding: "0 15px 0",
};
const bodyBottomStyle: Interpolation = {
  padding: "0 15px 150px",
};
const topDeeplinkStyle: Interpolation = {
  padding: `0 15px ${spacing.l}`,
  ".web-app-bible & a": {
    color: constantSemanticColors.onColorHighest,
  },
};
const bottomDeeplinkStyle: Interpolation = {
  padding: `${spacing.l} 15px 0`,
  ".web-app-bible & a": {
    color: constantSemanticColors.onColorHighest,
  },
};

type BibleDisplayProps = {
  css: string;
  html: string;
  highlightVerseNo?: number;
  book: BibleBookId;
  chapter: number;
  translationId: number;
  translationName: string;
  deeplinks: BibleDeeplinks;
};

export const BibleDisplay = ({
  css,
  html,
  highlightVerseNo,
  book,
  chapter,
  translationName,
  translationId,
  deeplinks,
}: BibleDisplayProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const { setRtl } = useContext(BibleLanguageContext);
  const { renderedTheme } = useTheme();
  const entryTracker = useContext(BibleEntry);

  const [, setLastPage] = useLocalStorageState({
    key: "biblePage",
    defaultValue: `${book}/${chapter}`,
  });

  useEffect(() => {
    entryTracker.setData(translationId, translationName, book, chapter);

    return () => {
      entryTracker.setData(null, null, null, null);
    };
  }, [book, chapter, translationName, translationId]);

  useEffect(() => {
    setLastPage(`${book}/${chapter}`);
  }, [book, chapter]);

  const scrollToVerse = () => {
    if (!ref.current) {
      setTimeout(scrollToVerse, 0);
      return;
    }
    const target = ref.current.querySelector(`.verse-${highlightVerseNo}`);
    setTimeout(
      () =>
        target &&
        target.scrollIntoView({ block: "center", behavior: "smooth" }),
      250,
    );
  };

  useEffect(() => {
    if (highlightVerseNo) {
      setTimeout(scrollToVerse, 0);
    }
  }, [html, highlightVerseNo]);

  useEffect(() => {
    setRtl(/class="[\w\s]*?rtl[\w\s]*?"/gi.test(html));
  }, [html]);

  const clearHighlight = (el: Element) => {
    const HIGHLIGHT_TIME_MS = 3000;
    setTimeout(() => {
      el.classList.remove("highlight");
    }, HIGHLIGHT_TIME_MS);
  };

  const handleLinkScroll: MouseEventHandler<HTMLDivElement> = (e) => {
    const anchor = (e.target as HTMLElement).closest("a");
    const button = (e.target as HTMLElement).closest("button");
    if (anchor?.href?.length) {
      const target = new URL(anchor.href, location.href);
      if (target.hash) {
        e.preventDefault();
        e.stopPropagation();
        const destinationId = target.hash.replaceAll("#", "");
        const destination = document.getElementById(destinationId);
        if (!destination) return;
        destination.scrollIntoView({ block: "center", behavior: "smooth" });

        let linkType:
          | "to-footnote"
          | "to-endnote"
          | "to-verse"
          | "to-marker"
          | null = null;
        if (/^\d+$/.test(destinationId)) {
          linkType = "to-verse";
        } else if (
          destinationId.startsWith("rfn") ||
          destinationId.startsWith("ren")
        ) {
          linkType = "to-marker";
        } else if (destinationId.startsWith("fn")) {
          linkType = "to-footnote";
        } else if (destinationId.startsWith("en")) {
          linkType = "to-endnote";
        }

        let highlightTargets: Element[] | null = null;
        switch (linkType) {
          case "to-footnote": {
            highlightTargets = [destination.closest(".footnote")].filter(
              Boolean,
            );
            break;
          }
          case "to-endnote": {
            highlightTargets = [destination.closest(".endnote")].filter(
              Boolean,
            );
            break;
          }
          case "to-verse": {
            const singleWrapper = destination.closest(".verseWrapper");
            if (singleWrapper) {
              highlightTargets = Array.from(
                document.querySelectorAll(
                  Array.from(singleWrapper.classList)
                    .map((c) => `.${c}`)
                    .join(""),
                ),
              );
            }
            break;
          }
          case "to-marker": {
            highlightTargets = [destination.closest("span")].filter(Boolean);
            break;
          }
        }
        if (highlightTargets?.length) {
          for (const el of highlightTargets) {
            el.classList.add("highlight");
            clearHighlight(el);
          }
        }
      }
    } else if (button?.classList?.contains("collapse-button")) {
      button.closest(".collapsible")?.classList?.toggle("collapsed");
    }
  };

  const [html1, html2, html3] = useBibleHTML(html);

  // Note: ideally, we wouldn't put a <style> tag inside the <body>, but Next's <Suspense> and <Head>
  // components are diametrically opposed, and <Head> always wins, so if we try to put the <style>
  // tag in the <Head>, it comes out undefined because it never waits for the promise to resolve.
  return (
    <>
      <Suspense>
        <style dangerouslySetInnerHTML={{ __html: css }} />
        {/* This behavior is different from the mobile apps because we don't like to manipulate the DOM directly */}
        {highlightVerseNo ? (
          <style>{`.verse-${highlightVerseNo} { background: yellow; color: black; transition-duration: 0; }`}</style>
        ) : null}
        {/* This is to support notes in the NABRE */}
        <style>{`.web-app-bible .highlight { background: yellow; color: black; transition-duration: 250ms; transition-timing-function: ease-in; }`}</style>
        <div className={"web-app-bible"} onClick={handleLinkScroll}>
          <div
            className={`${renderedTheme === "dark" ? "darkMode" : ""} ${html1?.includes("with-notes") ? "with-notes" : ""}`}
          >
            <div
              css={[bodyStyle, bodyTopStyle]}
              dangerouslySetInnerHTML={{ __html: html1 }}
            />
            {deeplinks?.top_content_deeplink ? (
              <div css={[bodyStyle, topDeeplinkStyle]}>
                <RecommendationDeeplink {...deeplinks.top_content_deeplink} />
              </div>
            ) : null}
            <div
              css={[bodyStyle, bodyMiddleStyle]}
              ref={ref}
              dangerouslySetInnerHTML={{ __html: html2 }}
            />
            {deeplinks?.bottom_content_deeplink ? (
              <div css={[bodyStyle, bottomDeeplinkStyle]}>
                <RecommendationDeeplink
                  {...deeplinks.bottom_content_deeplink}
                />
              </div>
            ) : null}
            <div
              css={[bodyStyle, bodyBottomStyle]}
              dangerouslySetInnerHTML={{ __html: html3 }}
            />
          </div>
        </div>
      </Suspense>
    </>
  );
};
